import { Component, computed, inject, input, linkedSignal, resource, signal } from '@angular/core';
import { TenantService, TranslatePipe } from '@cmi/addin-core';
import { LoadingSpinner, Notification } from '@cmi/ui';
import {
  DocumentFields,
  FieldValueChange,
  FieldValues,
  missingMandatory,
  withoutUnsupportedFields
} from '../document-fields/document-fields';
import {
  FieldConfigResponse,
  FieldPrefillResponse
} from '../document-fields/document-fields.models';
import { FieldConfigService } from '../document-fields/field-config.service';
import { DossierSearch } from '../dossiers/dossier-search';
import { DokumenteExplorerFolder, Dossier, DossierDetail } from '../dossiers/dossier.models';
import { DossierService } from '../dossiers/dossier.service';
import { folderPath } from '../dossiers/folder-path';
import { FolderTree } from '../dossiers/folder-tree';
import { NewFolderDraft, PLANNED_FOLDER_GUID, PlannedFolder } from '../dossiers/new-folder-form';
import { FolderTarget } from '../mail-upload/filing-pipeline.service';
import { StepPanel } from '../step-panel/step-panel';

/** What the user gets to fill in of the mask, and what it starts out with. */
export interface FieldForm {
  form: FieldConfigResponse;
  values: FieldValues;
  /** Fields of the form each mail fills in itself, shown but not filled in by the user. */
  locked: ReadonlySet<number>;
}

/** What the dossier proposals are loaded for. */
interface ProposalKey {
  address: string;
  tenant: string | null;
}

/** The mask, which fields the tenant fills in from the mail, and the form made of them. */
export interface LoadedFieldConfig extends FieldForm {
  config: FieldConfigResponse;
  prefill: FieldPrefillResponse;
}

/** folders with child added below the folder parentGuid names. */
function withFolder(
  folders: DokumenteExplorerFolder[],
  parentGuid: string,
  child: DokumenteExplorerFolder
): DokumenteExplorerFolder[] {
  return folders.map((folder) => ({
    ...folder,
    children:
      folder.guid === parentGuid
        ? [...folder.children, child]
        : withFolder(folder.children, parentGuid, child)
  }));
}

/**
 * Where a mail goes and with which document fields: dossier, folder, fields.
 * The state of the filing steps, shared by single and batch filing, which
 * differ only in what of the mask the user fills in and with what it starts.
 * Made in the host's injection context; {@link FilingSteps} shows it.
 */
export class FilingStepsState {
  private readonly _dossierService = inject(DossierService);
  private readonly _fieldConfigService = inject(FieldConfigService);
  private readonly _tenantService = inject(TenantService);

  /** Bound into the dossier search, so a reset can clear what was typed. */
  public readonly searchTerm = signal<string>('');
  public readonly selectedDossier = signal<Dossier | null>(null);
  /** Null files into the dossier root, whether picked as such or not picked at all. */
  public readonly selectedFolder = signal<DokumenteExplorerFolder | null>(null);
  /** The root picked on purpose, which closes the step like a folder does. */
  public readonly rootSelected = signal(false);
  /** The form for a new folder, while it is open. */
  public readonly newFolder = signal<NewFolderDraft | null>(null);
  /**
   * The new folder taken from the form, created only once a mail is filed
   * into it. It lasts only while it is the target, so the tree never shows a
   * new folder that filing would not create.
   */
  public readonly plannedFolder = signal<PlannedFolder | null>(null);

  public readonly dossierDetail = resource<
    DossierDetail | undefined,
    { guid: string | null; tenant: string | null }
  >({
    params: () => ({
      guid: this.selectedDossier()?.guid ?? null,
      tenant: this._tenantService.selectedTenantName()
    }),
    loader: (param) => {
      const guid = param.params.guid;
      return guid === null ? Promise.resolve(undefined) : this._dossierService.getDossier(guid);
    }
  });

  /**
   * Idles without an address, as in batch filing. A computed of its own with
   * an equality check, so the next mail from the same sender does not reload.
   */
  private readonly _proposalKey = computed(
    (): ProposalKey | undefined => {
      const address = this._proposalAddress();

      return address === null
        ? undefined
        : { address, tenant: this._tenantService.selectedTenantName() };
    },
    { equal: (a, b) => a?.address === b?.address && a?.tenant === b?.tenant }
  );

  /** The Geschäfte for the mail's address, proposed before anything is typed. */
  public readonly proposals = resource<Dossier[], ProposalKey | undefined>({
    params: this._proposalKey,
    loader: ({ params }) => this._loadProposals(params.address),
    defaultValue: []
  });

  /** The dossier's folders, the planned folder among them under its own stand-in guid. */
  public readonly folders = computed(() => {
    const explorer = this.dossierDetail.value()?.dokumenteExplorer ?? [];
    const planned = this.plannedFolder();
    const dossier = this.selectedDossier();

    if (!planned || !dossier) {
      return explorer;
    }

    const node: DokumenteExplorerFolder = {
      guid: PLANNED_FOLDER_GUID,
      titel: planned.titel,
      children: []
    };

    return planned.parentGuid === dossier.guid
      ? [...explorer, node]
      : withFolder(explorer, planned.parentGuid, node);
  });

  /** One new folder at a time: none while one is planned or its form is open. */
  public readonly canCreateFolder = computed(
    () => this.newFolder() === null && this.plannedFolder() === null
  );

  /**
   * What a mail is filed into: a folder's guid, null for the dossier root, or
   * for the planned folder a call that creates it. The call creates it once,
   * however many mails are filed with it, and again only after a failure.
   */
  public readonly folderTarget = computed<FolderTarget>(() => {
    const folder = this.selectedFolder();
    const planned = this.plannedFolder();
    const dossier = this.selectedDossier();

    if (folder?.guid !== PLANNED_FOLDER_GUID || !planned || !dossier) {
      return folder?.guid ?? null;
    }

    let created: Promise<string> | undefined;

    return (): Promise<string> =>
      (created ??= this._createPlannedFolder(dossier, planned).catch((error: unknown) => {
        created = undefined;
        throw error;
      }));
  });

  /**
   * The folders below the dossier root, which the tree shows as a node of its
   * own so it can be picked and created in. Carries the dossier's guid, which
   * no folder shares.
   */
  public readonly folderTree = computed<DokumenteExplorerFolder[]>(() => {
    const dossier = this.selectedDossier();

    return dossier ? [{ guid: dossier.guid, titel: dossier.titel, children: this.folders() }] : [];
  });

  /** What the tree marks as picked, the root standing in for its dossier's guid. */
  public readonly selectedTreeGuid = computed(
    () =>
      this.selectedFolder()?.guid ??
      (this.rootSelected() ? this.selectedDossier()?.guid : null) ??
      null
  );

  /**
   * The same mask for every dossier, so it is keyed on the tenant alone and
   * idles until a dossier is picked. A computed of its own, since `resource`
   * reloads whenever its params function re-runs, even to the same key: picking
   * another dossier must not refetch the mask.
   */
  private readonly _fieldConfigKey = computed(() =>
    this.selectedDossier() === null ? undefined : (this._tenantService.selectedTenantName() ?? '')
  );

  public readonly fieldConfig = resource<LoadedFieldConfig, string | undefined>({
    params: this._fieldConfigKey,
    loader: async () => {
      const [response, prefill] = await Promise.all([
        this._fieldConfigService.getFieldConfig(),
        this._loadFieldPrefill()
      ]);
      const config = withoutUnsupportedFields(response);

      return { config, prefill, ...this._toForm(config, prefill) };
    }
  });

  /**
   * What the user entered into the form, keyed by the ids FieldConfig sent.
   * Starts over from the form's values whenever the mask (re)loads.
   */
  public readonly fieldValues = linkedSignal<FieldValues>(() =>
    this.fieldConfig.hasValue() ? this.fieldConfig.value().values : {}
  );

  /**
   * Nothing can be filed before the mask is known, nor with a mandatory field
   * of the form empty. A locked field is left to each mail.
   */
  public readonly fieldsComplete = computed(() => {
    const loaded = this.fieldConfig.hasValue() ? this.fieldConfig.value() : undefined;

    return (
      !!loaded &&
      missingMandatory(loaded.form, this.fieldValues()).every((field) =>
        loaded.locked.has(field.fieldId)
      )
    );
  });

  public readonly selectedDossierSummary = computed(() => {
    const dossier = this.selectedDossier();

    return dossier ? `${dossier.titel} · ${dossier.laufnummer}` : null;
  });

  public readonly selectedFolderPath = computed(() => {
    const guid = this.selectedFolder()?.guid;
    const path = guid ? folderPath(this.folders(), guid) : null;

    return path ? path.join(' › ') : null;
  });

  /**
   * `toForm` runs as the mask loads: what of it the user fills in, and with what it starts.
   * `proposalAddress` is the address the dossiers are proposed for; the
   * proposals reload whenever it changes.
   */
  public constructor(
    private readonly _toForm: (
      config: FieldConfigResponse,
      prefill: FieldPrefillResponse
    ) => FieldForm,
    private readonly _proposalAddress: () => string | null = () => null
  ) {}

  public selectDossier(dossier: Dossier): void {
    this.selectedDossier.set(dossier);
    this._clearFolder();
  }

  /**
   * A folder of the tree, or its root node, which stands for the dossier root.
   * Any pick but the planned folder drops it.
   */
  public selectFolder(folder: DokumenteExplorerFolder): void {
    const isRoot = folder.guid === this.selectedDossier()?.guid;

    if (folder.guid !== PLANNED_FOLDER_GUID) {
      this.plannedFolder.set(null);
    }

    this.selectedFolder.set(isRoot ? null : folder);
    this.rootSelected.set(isRoot);
  }

  /** Opens the form for a new folder below parent, the root node included. */
  public startNewFolder(parent: DokumenteExplorerFolder): void {
    if (this.canCreateFolder()) {
      this.newFolder.set({ parentGuid: parent.guid, title: '' });
    }
  }

  /** Kept here rather than in the form, so it survives the step closing and reopening. */
  public changeNewFolderTitle(title: string): void {
    this.newFolder.update((draft) => draft && { ...draft, title });
  }

  public discardNewFolder(): void {
    this.newFolder.set(null);
  }

  /** Takes the folder of the form into the tree and picks it; nothing is created yet. */
  public planNewFolder(): void {
    const draft = this.newFolder();
    const titel = draft?.title.trim();

    if (!draft || !titel) {
      return;
    }

    this.plannedFolder.set({ parentGuid: draft.parentGuid, titel });
    this.newFolder.set(null);
    this.selectFolder({ guid: PLANNED_FOLDER_GUID, titel, children: [] });
  }

  /** Drops the planned folder, and the pick with it when it was the target. */
  public removePlannedFolder(): void {
    this.plannedFolder.set(null);

    if (this.selectedFolder()?.guid === PLANNED_FOLDER_GUID) {
      this.selectedFolder.set(null);
    }
  }

  public changeFieldValue(change: FieldValueChange): void {
    this.fieldValues.update((values) => ({ ...values, [change.fieldId]: change.value }));
  }

  /** Back to no dossier, no folder, nothing typed. */
  public reset(): void {
    this.selectedDossier.set(null);
    this._clearFolder();
    this.searchTerm.set('');
    this.fieldValues.set({});
  }

  /**
   * Creates the planned folder and puts it into the tree in its place, picked
   * still if it was. Added in place rather than by reloading the dossier,
   * which would swap the tree for a spinner. Left alone when another dossier
   * or tenant owns the tree by then; the mail still goes into the new folder.
   */
  private async _createPlannedFolder(dossier: Dossier, planned: PlannedFolder): Promise<string> {
    const tenant = this._tenantService.selectedTenantName();
    const created = await this._dossierService.createFolder(
      dossier.guid,
      planned.titel,
      planned.parentGuid === dossier.guid ? null : planned.parentGuid
    );

    if (this.selectedDossier() !== dossier || this._tenantService.selectedTenantName() !== tenant) {
      return created.guid;
    }

    const folder: DokumenteExplorerFolder = { ...created, children: [] };

    // A dossier reloading meanwhile brings the folder along from the server.
    if (!this.dossierDetail.isLoading()) {
      this.dossierDetail.update(
        (detail) =>
          detail && {
            ...detail,
            dokumenteExplorer:
              planned.parentGuid === dossier.guid
                ? [...detail.dokumenteExplorer, folder]
                : withFolder(detail.dokumenteExplorer, planned.parentGuid, folder)
          }
      );
    }

    if (this.plannedFolder() === planned) {
      this.plannedFolder.set(null);

      if (this.selectedFolder()?.guid === PLANNED_FOLDER_GUID) {
        this.selectedFolder.set(folder);
      }
    }

    return created.guid;
  }

  private _clearFolder(): void {
    this.selectedFolder.set(null);
    this.rootSelected.set(false);
    this.newFolder.set(null);
    this.plannedFolder.set(null);
  }

  /** The prefill is a convenience: failing it costs the prefill, not the mask. */
  private async _loadFieldPrefill(): Promise<FieldPrefillResponse> {
    try {
      return await this._fieldConfigService.getFieldPrefill();
    } catch (error) {
      console.warn('Loading the field prefill failed, no field is filled in from the mail:', error);
      return { felder: [] };
    }
  }

  /** The proposals are a shortcut: failing them leaves the search. */
  private async _loadProposals(address: string): Promise<Dossier[]> {
    try {
      return (await this._dossierService.getSenderDossiers(address)).dossierAbsender ?? [];
    } catch (error) {
      console.warn('Loading the Dossier proposals failed, only the search is shown:', error);
      return [];
    }
  }
}

/** The step panels of {@link FilingStepsState}: dossier, folder, document fields. */
@Component({
  selector: 'cmi-filing-steps',
  imports: [
    TranslatePipe,
    LoadingSpinner,
    Notification,
    DossierSearch,
    FolderTree,
    DocumentFields,
    StepPanel
  ],
  templateUrl: './filing-steps.html',
  host: { class: 'flex flex-col gap-3' }
})
export class FilingSteps {
  public readonly steps = input.required<FilingStepsState>();
}
